/**
 * The guided Call Script flow (middle column of the Human Call tab).
 *
 * Renders the script's {@link ScriptUnit}s as step cards with:
 * - A numbered badge (→ ✓ when done)
 * - The section title and a status pill
 * - Current step: highlighted with `border-primary` (a non-layout-shifting cue).
 * - Every card is collapsible and FULLY USER-CONTROLLED: the first actionable step
 *   (+ intro/pinned sections) opens on initial mount; after that only the user opens
 *   or collapses sections. The system NEVER auto-opens/collapses as the current step
 *   advances — that coupling made the script jump whenever the required-set changed.
 *
 * A small collapsible "fields already on file" prefilled summary sits at the
 * top so the worker sees what's already captured before working the questions.
 * (Hidden in `all` mode — every prefilled field then renders inline in its own
 * section, so the strip would only duplicate them.)
 *
 * `filterMode` (BV-2129) controls how much of the script is revealed, mirroring the
 * Output board's segmented filter: `all` shows every section with every field,
 * `missed` only the fields still uncaptured, `required` only those required for the
 * current scenario. `all` is the default (BI-1670). The rule itself lives in
 * `partitionScriptUnits` (pure + unit-tested); this component stays a renderer.
 *
 * Empty-state ("No required questions for this scenario yet.") when no visible
 * units AND no otherFields.
 *
 * Composition only — the editable controls come from {@link ScriptStep}.
 * Design tokens only (no raw Tailwind colors).
 */
import { Pin } from 'lucide-react';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { CloneControls, type UseCloneItemsResult } from '@/components/fields/useCloneItems';
import { cn } from '@/utils';
import {
  partitionScriptUnits,
  type ResolvedField,
  type ScriptFilterMode,
  type ScriptUnit,
  unitNeedsAttention,
  unitProgress,
} from '@/utils/humanCallModel';

import { ScriptStep, unitBadge } from './ScriptStep';

interface ScriptFlowProps {
  units: ScriptUnit[];
  /** The id of the first unit with a missing field; drives current-step open/highlight. */
  currentUnitId: string | null;
  /** Required-but-empty fields not covered by any question (the catch-all section). */
  otherFields: ResolvedField[];
  readOnly: boolean;
  /** True only while the call is live (CALL_STATUS_IN_PROGRESS). Gates mark-heard in step fields. */
  callActive: boolean;
  heardAt: (key: string) => number | undefined;
  callStartMs?: number;
  onFieldChange: (key: string, value: unknown) => void;
  /** Commit the disposition snapshot (fired on field blur for text inputs). */
  onFieldCommit: (key?: string) => void;
  onToggleHeard: (key: string) => void;
  caseRecordId?: string;
  /** Output metadata `fields` tree — drives per-field evidence / chain-of-thought /
   *  heard-seek once extraction has run (BV-1575). */
  outputMetaFields?: Record<string, unknown>;
  /** The call's EXTRACTED metadata `fields` tree — the source that actually carries
   *  per-field evidence/reasoning. Same path keys as the data tree. */
  extractedMetaFields?: Record<string, unknown>;
  /** The extracted VALUES for this call — powers the per-field Accept affordance. */
  extractedData?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  /** Validation messages keyed by field key (BV-2129), forwarded to every step. */
  fieldErrors?: Record<string, string>;
  /** Which slice of the script to reveal (BV-2129) — 'all' (the default, BI-1670),
   *  'missed' or 'required'. Owned by the panel header's segmented filter. */
  filterMode?: ScriptFilterMode;
  /**
   * Unit-level clone controllers keyed by `questionId` (BV-1575) — copy this question's
   * answers onto its other entity instances. A missing or null entry renders no control,
   * so the flow degrades to its pre-clone behaviour when read-only or when the question
   * fanned out to a single instance. Indexed by the unit's `instance.index`.
   */
  unitClones?: Record<string, UseCloneItemsResult | null>;
  /** Reports the collapse-all control's state up so the panel header can render the
   *  toggle button inline with the "Call Script" title. `count` is the number of
   *  collapsible step cards (0 → hide the button); `anyOpen` drives the icon/label. */
  onCollapsibleChange?: (state: { count: number; anyOpen: boolean }) => void;
}

/** Imperative handle: lets the panel header trigger collapse-all / expand-all. */
export interface ScriptFlowHandle {
  toggleAll: () => void;
  /**
   * Scroll the script to one field — what a click on an Output board row asks for.
   * Opens the field's step if it is collapsed (its section sub-group reopens itself);
   * a no-op when the field is not in the rendered script (not referenced by any
   * question, or narrowed out by the filter).
   */
  revealField: (key: string) => void;
}

/**
 * Accordion header for a unit: the question's `section` when set, else the first
 * line of the question prompt. Entity units append the instance code/name.
 */
function unitLabel(unit: ScriptUnit): string {
  const heading = unit.section.trim() || unit.prompt.split('\n')[0]?.trim() || unit.questionId;
  if (unit.instance) {
    const codePart = unit.instance.name
      ? `${unit.instance.code} · ${unit.instance.name}`
      : unit.instance.code;
    return `${heading} (${codePart})`;
  }
  return heading;
}

/**
 * Format a prefilled field's value for an "already on file" chip, using the
 * field's own options for enum-label lookup (e.g. PLAN_STATUS_ACTIVE → "Active").
 */
function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const ScriptFlowComponent = React.forwardRef<ScriptFlowHandle, ScriptFlowProps>(
  function ScriptFlowInner(
    {
      units,
      currentUnitId,
      otherFields,
      readOnly,
      callActive,
      heardAt,
      callStartMs,
      onFieldChange,
      onFieldCommit,
      onToggleHeard,
      caseRecordId,
      outputMetaFields,
      extractedMetaFields,
      extractedData,
      onNavigateToCall,
      onNavigateToAudit,
      unitClones,
      fieldErrors,
      filterMode = 'all',
      onCollapsibleChange,
    },
    ref,
  ) {
    // Partition built units into the intro/pinned set (rendered first, always, with ALL
    // their fields) and the required-only flow. The required flow is unchanged: only
    // sections carrying a required field are shown, narrowed to those required fields —
    // buildScriptModel rebuilds on every output/disposition change, so a field flipping
    // to required makes its section AND row appear automatically (and vice-versa). The
    // intro-vs-required rule lives entirely in partitionScriptUnits (pure + unit-tested).
    // In 'all' mode — the default (BI-1670) — that same function reveals every section
    // with all its fields instead of a narrowed subset; 'missed' and 'required' narrow
    // to uncaptured and required fields respectively, once the rep picks them in the
    // header's segmented filter.
    const { introUnits, visibleUnits } = useMemo(
      () => partitionScriptUnits(units, filterMode),
      [units, filterMode],
    );
    const introIds = useMemo(() => new Set(introUnits.map(u => u.id)), [introUnits]);
    const visibleIds = useMemo(() => new Set(visibleUnits.map(u => u.id)), [visibleUnits]);

    // effectiveCurrentId: highlight the current step only if it's actually rendered
    // (in the intro or required-visible set).
    const effectiveCurrentId =
      currentUnitId && (visibleIds.has(currentUnitId) || introIds.has(currentUnitId))
        ? currentUnitId
        : null;

    // Open/collapse is FULLY USER-CONTROLLED. `openIds` is seeded ONCE on mount with the
    // initial current step + intro/pinned sections; thereafter only user clicks change it.
    // The system never opens/collapses a section as `currentUnitId` advances — that
    // coupling made the script jump whenever the required-set changed. `currentUnitId`
    // still drives the highlight/badge below (a visual "next" cue that shifts no layout).
    const [openIds, setOpenIds] = useState<Set<string>>(() => {
      const seed = new Set<string>(introIds);
      if (effectiveCurrentId) seed.add(effectiveCurrentId);
      return seed;
    });
    const isOpen = (id: string) => openIds.has(id);
    const toggleStep = (id: string) =>
      setOpenIds(s => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });

    // Fields with a PRIOR value (prefilled — from earlier AI calls, not edited this
    // session). These are the fields the script can skip; shown as read-only chips.
    // Deduped by key across units.
    const prefilled = useMemo<ResolvedField[]>(() => {
      const seen = new Set<string>();
      const out: ResolvedField[] = [];
      for (const u of units) {
        // Intro units render all their own fields inline (always), so don't also list
        // them on the "already on file" strip — that would duplicate the same key.
        if (u.alwaysShow) continue;
        for (const f of u.fields) {
          if (f.status === 'prefilled' && !seen.has(f.key)) {
            seen.add(f.key);
            out.push(f);
          }
        }
      }
      return out;
    }, [units]);

    const [showOnFile, setShowOnFile] = useState(false);

    // Panel-level collapse-all / expand-all over the rendered step accordions (intro +
    // required-visible units). otherFields renders always-open and isn't toggled here.
    // The control itself is rendered by the panel header (inline with the "Call Script"
    // title): we expose `toggleAll` via the ref and report `{count, anyOpen}` up so the
    // header can show the button and the correct icon/label.
    const collapsibleIds = useMemo(
      () => [...introUnits, ...visibleUnits].map(u => u.id),
      [introUnits, visibleUnits],
    );
    const anyOpen = collapsibleIds.some(id => openIds.has(id));

    // The field a reveal is heading for. Only recorded here: the row scrolls ITSELF once
    // it has rendered (see ScriptStep's FieldRow) and clears this through `onRevealed`,
    // since the step and its section sub-group may both need to open first.
    const [revealKey, setRevealKey] = useState<string | null>(null);
    const onRevealed = useCallback(() => setRevealKey(null), []);

    useImperativeHandle(
      ref,
      () => ({
        toggleAll: () => setOpenIds(anyOpen ? new Set() : new Set(collapsibleIds)),
        revealField: (key: string) => {
          const hasKey = (fields: ResolvedField[]) => fields.some(f => f.key === key);
          const unit = [...introUnits, ...visibleUnits].find(u => hasKey(u.fields));
          // "Other required fields" is always open, so it needs no step to open.
          if (!unit && !hasKey(otherFields)) return;
          if (unit) setOpenIds(prev => (prev.has(unit.id) ? prev : new Set(prev).add(unit.id)));
          setRevealKey(key);
        },
      }),
      [anyOpen, collapsibleIds, introUnits, visibleUnits, otherFields],
    );
    useEffect(() => {
      onCollapsibleChange?.({ count: collapsibleIds.length, anyOpen });
    }, [collapsibleIds.length, anyOpen, onCollapsibleChange]);

    // Early-return empty-state (all hooks must be ABOVE this). An intro-only script is
    // NOT empty — pinned sections render even with no required questions.
    if (introUnits.length === 0 && visibleUnits.length === 0 && otherFields.length === 0) {
      return (
        <p className="px-1 py-8 text-center text-sm text-muted-foreground">
          No required questions for this scenario yet.
        </p>
      );
    }

    // Render one step card. `leading` is the badge-circle content for the not-done
    // state: a 1-based number for the required flow, a pin glyph for intro/pinned units
    // (which aren't part of the numbered sequence). Done → ✓ either way.
    const renderUnit = (unit: ScriptUnit, leading: React.ReactNode) => {
      const isCurrent = unit.id === effectiveCurrentId;
      // Current/intro steps open by default; the user can collapse or expand any.
      const open = isOpen(unit.id);
      const badge = unitBadge(unit);
      // ✓ when the section is done — every required field filled OR heard (heard counts).
      const isDone = unitProgress(unit).state === 'done';
      // Blue left accent (BI-1464): section is required and still has work left
      // (pending / to-collect). Falls away when done or when nothing was required.
      const needsAttention = unitNeedsAttention(unit);

      return (
        <section
          key={unit.id}
          className={cn(
            'mb-3 overflow-hidden rounded-xl border transition-colors',
            isCurrent
              ? 'border-primary bg-card shadow-sm'
              : isDone
                ? 'border-border bg-muted/30'
                : 'border-border bg-card',
            // Layers on top of the current-step ring / done background — a distinct
            // left bar, so a required-incomplete section stands out in the flow.
            needsAttention && 'border-l-4 border-l-primary',
          )}
        >
          {/* Step header — always visible; click to collapse/expand this step */}
          <div
            className="flex cursor-pointer select-none items-center gap-2.5 px-4 py-3"
            onClick={() => toggleStep(unit.id)}
          >
            {/* Badge: number (required flow) or pin (intro) → ✓ when done */}
            <span
              className={cn(
                'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                isDone ? 'bg-positive/40 text-positive-foreground' : 'bg-primary/10 text-primary',
              )}
            >
              {isDone ? '✓' : leading}
            </span>

            {/* Step title — always foreground (not muted, even when done) */}
            <span className="flex-1 truncate text-[13px] font-semibold text-foreground">
              {unitLabel(unit)}
            </span>

            {/* Unit-level clone (BV-1575) — only fanned-out units carry an instance, so
                top-level steps never show it. CloneControls renders nothing when the
                controller is null, and stops click propagation so its buttons do not
                toggle this step open/closed. */}
            {unit.entityKey && unit.instance && (
              <CloneControls
                index={unit.instance.index}
                clone={unitClones?.[unit.questionId] ?? null}
                entityLabel={unit.entityKey}
                subject="answers to this question"
              />
            )}

            {/* Status COUNT pill (BI-1467): number of required fields in this section
                still to capture — green 0 when done, red count otherwise. Mirrors the
                count-pill color scheme (bg-positive/40 vs bg-critical/30). */}
            {badge && (
              <span
                className={cn(
                  'inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded px-1 text-[10px] font-semibold',
                  badge.done
                    ? 'bg-positive/40 text-positive-foreground'
                    : 'bg-critical/30 text-critical-foreground',
                )}
                aria-label={badge.done ? 'Section complete' : `${badge.count} fields to capture`}
              >
                {badge.count}
              </span>
            )}
          </div>

          {/* Step body — visible when open */}
          {open && (
            <ScriptStep
              fieldErrors={fieldErrors}
              revealKey={revealKey}
              onRevealed={onRevealed}
              unit={unit}
              readOnly={readOnly}
              callActive={callActive}
              heardAt={heardAt}
              callStartMs={callStartMs}
              onFieldChange={onFieldChange}
              onFieldCommit={onFieldCommit}
              onToggleHeard={onToggleHeard}
              caseRecordId={caseRecordId}
              outputMetaFields={outputMetaFields}
              extractedMetaFields={extractedMetaFields}
              extractedData={extractedData}
              onNavigateToCall={onNavigateToCall}
              onNavigateToAudit={onNavigateToAudit}
            />
          )}
        </section>
      );
    };

    return (
      <div>
        {/* Intro / pinned sections — always at the very TOP, above the on-file strip,
          open by default, with ALL their fields regardless of required/filled status. */}
        {introUnits.map(unit => renderUnit(unit, <Pin className="h-3 w-3" aria-hidden="true" />))}

        {/* Fields already captured by earlier AI calls — surfaced near the top so the
          worker sees what's already on file before working the questions below.
          Hidden in 'all' mode: those prefilled fields now render inline in their own
          sections, so the strip would only duplicate them. Kept in the narrowed modes,
          where they are filtered out of the flow and this is the only place they show. */}
        {filterMode !== 'all' && prefilled.length > 0 && (
          <div
            className="mb-4 cursor-pointer rounded-lg border border-dashed border-border px-3.5 py-2.5 text-xs text-muted-foreground"
            onClick={() => setShowOnFile(v => !v)}
          >
            <span className="font-semibold text-foreground">
              {prefilled.length} {prefilled.length === 1 ? 'field' : 'fields'} already on file
            </span>{' '}
            — the script skips them. Click to view.
            {showOnFile && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" onClick={e => e.stopPropagation()}>
                {prefilled.map(f => (
                  <span
                    key={f.key}
                    className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {f.field.label}: <b className="text-foreground">{formatValue(f.value)}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Required-only flow — numbered, unchanged behavior. */}
        {visibleUnits.map((unit, i) => renderUnit(unit, i + 1))}

        {/* Catch-all: required-but-empty fields no question covers. Editable so the
          worker can still capture them during the call; always open since required. */}
        {otherFields.length > 0 && (
          <section className="mb-3 overflow-hidden rounded-xl border border-attention-border bg-card">
            <div className="px-4 py-3 text-[13px] font-semibold text-foreground">
              Other required fields
            </div>
            <ScriptStep
              fieldErrors={fieldErrors}
              revealKey={revealKey}
              onRevealed={onRevealed}
              unit={{
                id: '__other__',
                questionId: '',
                section: '',
                prompt: '',
                helpText: 'Required by the schema but not part of the script.',
                entityKey: null,
                fields: otherFields,
                done: false,
                alwaysShow: false,
              }}
              readOnly={readOnly}
              callActive={callActive}
              heardAt={heardAt}
              callStartMs={callStartMs}
              onFieldChange={onFieldChange}
              onFieldCommit={onFieldCommit}
              onToggleHeard={onToggleHeard}
              caseRecordId={caseRecordId}
              outputMetaFields={outputMetaFields}
              extractedMetaFields={extractedMetaFields}
              extractedData={extractedData}
              onNavigateToCall={onNavigateToCall}
              onNavigateToAudit={onNavigateToAudit}
            />
          </section>
        )}
      </div>
    );
  },
);

// Memoized so non-keystroke re-renders (e.g. `isSaving` toggles, background
// refetches) are skipped when props are shallow-equal. The model/board props
// still change per keystroke by necessity, so those re-renders are preserved.
export const ScriptFlow = React.memo(ScriptFlowComponent);
ScriptFlow.displayName = 'ScriptFlow';
